import React, { useEffect, useState } from 'react';
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	Tooltip,
	ResponsiveContainer,
	CartesianGrid,
} from 'recharts';

const SimpleLineChart = ({
	data,
	xKey = 'date',
	yKey = 'value',
	color = '#1677ff',
	height = 260,
	withGrid = true,
	strokeWidth = 2,
	formatYAxis,
	formatTooltipValue,
	label,
	padDomain = true, // добавляем отступы по оси Y
	integerTicks = true, // показывать только целые значения
	customTooltip, // новый проп для кастомного контента тултипа
	fixedPadding = null, // фиксированный отступ сверху и снизу (например, 20)
}) => {
	const [isMobile, setIsMobile] = useState(
		typeof window !== 'undefined' ? window.innerWidth < 680 : false,
	);
	useEffect(() => {
		function onResize() {
			setIsMobile(window.innerWidth < 680);
		}
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	// Рассчитываем домен вручную, чтобы избежать случая min==max (ось 0..1 и т.п.) и прилипаний
	let domain = ['auto', 'auto'];
	let ticks = undefined;
	if (padDomain && Array.isArray(data) && data.length) {
		const values = data.map((d) => d?.[yKey]).filter((v) => typeof v === 'number');
		if (values.length) {
			let min = Math.min(...values);
			let max = Math.max(...values);

			// Если задан фиксированный отступ, используем его
			if (fixedPadding !== null && typeof fixedPadding === 'number') {
				const dataMax = max; // сохраняем оригинальное максимальное значение
				min = dataMax - fixedPadding;
				max = dataMax + fixedPadding;
				if (min < 0) min = 0; // убираем отрицательные значения
				// Устанавливаем домен без дополнительных округлений
				domain = [min, max];
				// Генерируем 5 тиков с равным интервалом
				const step = (max - min) / 4;
				ticks = [min, min + step, min + 2 * step, min + 3 * step, max];
			} else {
				// Упрощённая версия: вычисляем min/max + небольшой паддинг, но никогда не уходим ниже 0
				if (min === max) {
					const pad = min === 0 ? 1 : Math.max(1, Math.round(min * 0.4));
					min = min - pad;
					max = max + pad;
				} else {
					const range = max - min;
					const pad = Math.max(1, Math.round(range * 0.2));
					min = min - pad;
					max = max + pad;
				}

				if (min < 0) min = 0; // убираем отрицательные значения
				// Округляем границы до целых если нужно
				if (integerTicks) {
					min = Math.floor(min);
					max = Math.ceil(max);
					if (min === max) {
						// защита если после округления сжалось
						min = Math.max(0, min - 1);
						max = max + 1;
					}
				}
				// Если линия прилипла к низу (значения почти одинаковые и =0 после clamp)
				if (max <= 5 && min === 0) {
					max = max + 1; // небольшой воздух сверху
				}
				domain = [min, max];
			}
		}
	}

	const axisFont = { fontSize: isMobile ? 9 : 11, fill: '#475569' };
	const chartMargin = { top: 8, right: 12, bottom: isMobile ? 20 : 26, left: 4 };
	const containerStyle = {
		width: '100%',
		height,
		background: '#fff',
		border: '1px solid #eee',
		borderRadius: 12,
		padding: isMobile ? 12 : 16,
	};
	const hideFirstTick = Array.isArray(data) && data.length > 0 && data.length <= 7; // неделя

	return (
		<div style={containerStyle}>
			{label && <h4 style={{ margin: 0, marginBottom: 12 }}>{label}</h4>}
			<ResponsiveContainer>
				<LineChart data={data} margin={chartMargin}>
					{withGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}
					<XAxis
						dataKey={xKey}
						tick={axisFont}
						axisLine={{ stroke: '#cbd5e1' }}
						tickLine={false}
						tickFormatter={(val, idx) => (hideFirstTick && idx === 0 ? '' : val)}
					/>
					<YAxis
						domain={domain}
						ticks={ticks}
						tickFormatter={formatYAxis}
						allowDecimals={fixedPadding !== null ? true : !integerTicks}
						allowDataOverflow={false}
						tick={axisFont}
						axisLine={{ stroke: '#cbd5e1' }}
						tickLine={false}
					/>
					{customTooltip ? (
						<Tooltip content={customTooltip} />
					) : (
						<Tooltip formatter={(v) => (formatTooltipValue ? formatTooltipValue(v) : v)} />
					)}
					<Line
						type="monotone"
						dataKey={yKey}
						stroke={color}
						strokeWidth={strokeWidth}
						dot={{ r: isMobile ? 2.5 : 3 }}
						activeDot={{ r: isMobile ? 4 : 5 }}
					/>
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
};

export default SimpleLineChart;
